import React, { useCallback, useState, useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { getMediaObjectFromUploads } from 'actions/addPost';
import { reloadFeedAction } from 'actions/feed';
import { openGifModalAction } from 'actions/gifModal';
import useCommentReducer from 'components/Comment/useCommentReducer';
import PositiveNegativeSentimentToggle from 'components/PositiveNegativeSentimentToggle';
import TagRecommendations from 'components/TagRecommendations';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import EnterComment from 'components/feed/EnterComment';
import VisibilityComponent from 'components/feed/PostVisibilityComponent';
import { useTagRecommendations, useVisibilityManuallyUpdated } from 'components/hooks/post';
import { useMediaUploadStateComment } from 'components/hooks/useMediaUploadState';
import { EditorState } from 'draft-js';
import { useSelector, useDispatch } from 'react-redux';
import MediaPreview from 'screens/home/addPost/MediaPreview';
import UploadTypesView from 'screens/home/addPost/UploadTypesView';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { checkDenyPublicCFVisibilityEnabled } from 'utils/helpers/featureFlags';

import { ERR_MESS_CREATE_COMMENT, POST_VISIBILITY_TYPES } from 'constants/ProjectConstants';
import { sendClickedOnPraiseGoalsEvent } from 'constants/mixpanelHelper';

import {
  getStringifiedRawContentStateFromEditorState,
  updateEditorStatePredictTagsAndVisbility,
} from 'services/helpers/post';
import { createComment, getDetailsFromCommentState } from 'services/post';
import { showErrorSnackbar } from 'services/snackbar';

import { Switch } from 'uiToolkitV1/ToggleSwitch';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { QUERY_KEYS } from '../datahooks';
import OKREditCommentComponent from './OKRPostField';

const CommentSection = ({
  taskId,
  taskType,
  isCommentAllowed = true,
  addCommentFromOutsideToggle = false,
  setAddCommentFromOutsideToggle = () => {},
  handleCommentSaved = () => {},
  isEnterCommentAlwaysActive = false,
  parentSource = '',
  isCommentAllowedWithEnterButton = true,
  hideAvatar = false,
  placeholderText = '',
  contentState,
  media: initialMedia = [],
  visibility: initialVisibility,
  sentiment: initialSentiment,
  setModifiedCommentState = () => {},
  setIsCommentUpdateInProgress = () => {},
  isCommentUpdateInProgress = false,
  enableKudos = true,
  useEmojiFunctionality = true,
  allowEnterToNextLine = false,
  handleBlur = () => {},
  handleFocus = () => {},
}) => {
  const dispatch = useDispatch();
  const employeeId = useSelector(selectLoggedInEmployeeId);
  const queryClient = useQueryClient();
  const page = useViewPageName();
  const {
    commentState,
    setEditorState,
    toggleIsUpdating: toggleIsCommentUpdating,
    setUploadedFile,
    clearComment,
    setGif,
    updateVisibility,
    updateSentiment,
    addTag,
  } = useCommentReducer(contentState, initialMedia, initialVisibility, initialSentiment);

  const { recommendations, updateTags } = useTagRecommendations();

  useEffect(() => {
    if (addCommentFromOutsideToggle) {
      addComment();
      setAddCommentFromOutsideToggle(false);
    }
  }, [addCommentFromOutsideToggle]);

  const { editorState, images = [], gifs = [], isUpdating: isCommentUpdating, visibility, sentiment } = commentState;

  const resetCommentState = useCallback(() => {
    setEditorState(EditorState.createEmpty());
    setUploadedFile(null);
    setGif(null);
  }, [setEditorState, setUploadedFile, setGif]);

  useEffect(() => {
    if (contentState === null && editorState.getCurrentContent().hasText()) {
      resetCommentState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentState, resetCommentState]);

  const [isEnterCommentActive, setEnterCommentActive] = useState(false);

  const uploadedFile = images[0];
  const uploadedGif = gifs[0];

  const { fileUrl, deleteFile, uploadFile, uploading, setFileObject } = useMediaUploadStateComment({
    setImage: setUploadedFile,
    uploadedImage: uploadedFile,
    setGif,
  });

  const { visibilityManuallyUpdated, updateVisibilityManuallyStatus } = useVisibilityManuallyUpdated();

  const [commentAsKudos, setCommentAsKudos] = useState(false);

  const updateFeed = useCallback(() => {
    dispatch(
      reloadFeedAction({
        employeeId,
        pageType: 'HOME_PAGE',
      })
    );
  }, []);

  const loadComments = useCallback(
    (comment) => {
      updateFeed(); // to keep consistency, when we create a comment in task panel, it should also show in the feed
    },
    [updateFeed]
  );

  const addComment = useCallback(() => {
    const media = getMediaObjectFromUploads(images, gifs);
    const isCommentExists = editorState?.getCurrentContent()?.getPlainText('\n') || media?.length;

    if (!isCommentExists) {
      return;
    }

    if (isCommentUpdateInProgress) {
      return;
    }

    toggleIsCommentUpdating();
    //   if (taskType === TASK_TYPES_2.goal)
    //     sendCommentedOnKREvent({
    //       eventProperties: {
    //         goalId: taskId
    //       }
    //     });

    //   if (taskType2 !== TASK_TYPES_2.checkIn && taskType === TASK_TYPES_2.goal) {
    //     sendGoalEvent({
    //       page: { pageName: getPageName(location), flow: 'Activity' },
    //       eventName: 'commentedgoals',
    //       referrer: 'SidePanel'
    //     });
    //   }
    //   if (taskType2 !== TASK_TYPES_2.checkIn && taskType === TASK_TYPES_2.task) {
    //     sendTaskEvent({
    //       page: { pageName: getPageName(location), flow: 'Activity' },
    //       eventName: TASK_EVENTS.commented,
    //       referrer: 'SidePanel'
    //     });
    //   }

    //   if (taskType2 === TASK_TYPES_2.checkIn) {
    //     sendOneOnOneEvent({
    //       page: { pageName: getPageName(location), flow: 'Activity' },
    //       eventName: ONE_ON_ONE_EVENTS_OLD.commentedOnTask,
    //       referrer: 'SidePanel'
    //     });
    //   }

    setIsCommentUpdateInProgress(true);
    createComment({
      feedId: taskId,
      commentEditorState: editorState,
      media,
      feedType: taskType,
      visibilityType: visibility,
      // sentiment, removing this as this sentiment is body is deprecated
      isKudos: commentAsKudos,
    })
      .then(({ comment, reactions }) => {
        handleCommentSaved();
        if (typeof loadComments === 'function') loadComments(comment);
        setEnterCommentActive(false);
        setFileObject(null); // setting uploaded file object that is just in the local state to null
        if (typeof clearComment === 'function') clearComment();
        setTimeout(() => {
          queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
        }, 800);
        setIsCommentUpdateInProgress(false);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || ERR_MESS_CREATE_COMMENT));
        toggleIsCommentUpdating();
        setIsCommentUpdateInProgress(false);
      });

    // if (pageType) {
    //   sendPostEvent({ pageType, eventName: POST_EVENTS.commented });
    // }
  }, [
    clearComment,
    editorState,
    dispatch,
    taskId,
    loadComments,
    setFileObject,
    // pageType,
    setEnterCommentActive,
    gifs,
    images,
    toggleIsCommentUpdating,
    visibility,
    commentAsKudos,
    taskType,
  ]);

  const onEscape = useCallback(() => {
    clearComment();
    setEnterCommentActive(false);
  }, [clearComment, setEnterCommentActive]);

  function onEditorStateChange(newEditorState) {
    updateEditorStatePredictTagsAndVisbility({
      editorState,
      newEditorState,
      setEditorState,
      visibilityManuallyUpdated,
      onAttributePredictionSuccess: (tags) => {
        updateTags(tags);
      },
      onVisibilityPredictionSuccess: (visibility) => {
        updateVisibility(visibility);
      },
    });
  }

  function openGifModal() {
    dispatch(openGifModalAction({ onSelect: setGif }));
  }

  useEffect(() => {
    const media = getMediaObjectFromUploads(images, gifs);

    const newContentState = getStringifiedRawContentStateFromEditorState({
      editorState,
    });
    const isCommentExists = Boolean(editorState?.getCurrentContent()?.getPlainText('\n') || media?.length);
    const commentPayloadType = getDetailsFromCommentState({ commentEditorState: editorState, media, sentiment });
    commentPayloadType.visibilityType = visibility || POST_VISIBILITY_TYPES.global.name;

    setModifiedCommentState(newContentState, media, visibility, sentiment, isCommentExists, commentPayloadType);
  }, [contentState, editorState, gifs, images, sentiment, setModifiedCommentState, visibility]);

  return (
    <EnterComment
      isDisabled={!isCommentAllowed}
      isEnterCommentActive={isEnterCommentAlwaysActive || isEnterCommentActive}
      setIsEnterCommentActive={setEnterCommentActive}
      placeholder={
        commentAsKudos
          ? 'Use @ to tag someone and # to include a competency'
          : placeholderText || 'Add a comment or praise'
      }
      isLoggedInEmployeeComment
      EditCommentComponent={
        <StyledEditCommentComponent
          enableKudos={enableKudos}
          handleBlur={handleBlur}
          handleFocus={handleFocus}
          useEmojiFunctionality={useEmojiFunctionality}
          placeholder={
            commentAsKudos
              ? 'Use @ to tag someone and # to include a competency'
              : placeholderText || 'Add a comment or praise'
          }
          onEnter={isCommentAllowedWithEnterButton ? addComment : () => {}}
          allowEnterToNextLine={allowEnterToNextLine}
          onEscape={onEscape}
          editorState={editorState}
          setEditorState={onEditorStateChange}
          loading={isCommentUpdating}
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
          isDisabled={!isCommentAllowed}
          MediaPreview={
            <MediaPreview
              fileUrl={fileUrl}
              deleteFile={deleteFile}
              uploading={uploading}
              uploadFile={uploadFile}
              setGif={setGif}
              gifUrl={uploadedGif ? uploadedGif.url : null}
            />
          }
          UploadTypesView={
            <UploadTypesView
              uploadFile={uploadFile}
              uploadedImage={uploadedFile}
              toggleGifModal={openGifModal}
              gifUrl={uploadedGif ? uploadedGif.url : null}
              iconSize="24px"
            />
          }
          VisibilityComponent={
            <VisibilityComponent
              visibility={visibility}
              updateVisibility={updateVisibility}
              changeIsVisibilityUpdatedByUserStatus={updateVisibilityManuallyStatus}
            />
          }
          TagRecommendations={<TagRecommendations tagRecommendations={recommendations} addTag={addTag} />}
          SentimentComponent={
            <PositiveNegativeSentimentToggle selected={sentiment} updateSentiment={updateSentiment} />
          }
          KudosComponent={
            checkDenyPublicCFVisibilityEnabled() ? null : (
              <Box display="flex">
                <Tooltip label="Comment as a Praise and notify everyone" shouldWrapChildren>
                  <Switch
                    isChecked={commentAsKudos}
                    onChange={(event) => {
                      setCommentAsKudos(event.target.checked);
                      sendClickedOnPraiseGoalsEvent({ eventProperties: { source: parentSource, page } });
                    }}
                    isDisabled={!isCommentAllowed}
                  />
                </Tooltip>

                <TextBodyMedium ml="10px">Praise</TextBodyMedium>
              </Box>
            )
          }
        />
      }
      avatarSize={24}
      hideAvatar={hideAvatar}
    />
  );
};

export default CommentSection;

const StyledEditCommentComponent = styled(OKREditCommentComponent)`
  background: white;
`;
